import { useQuery } from '@tanstack/react-query';

import { isEmpty } from 'utils/common';
import { buildApiURL } from 'utils/network';
import { refreshRates } from 'config';

const fetchSystem = async (agent, signal, view) => {
  if (isEmpty(agent)) {
    return {};
  }

  // Connect and get data
  const [resp, verResp] = await Promise.all([
    fetch(buildApiURL(agent, view, 'system'), { signal }),
    fetch(buildApiURL(agent, view, 'system/version'), { signal })
  ]);
  var data = await resp.json();
  const version = await verResp.json();
  data.version = version.version;
  return data;
};

const fetchOverview = async (agent, signal, view) => {
  if (isEmpty(agent)) {
    return {};
  }

  // Connect and get data
  try {
    const [resp, respMem, respSwap] = await Promise.all([
      fetch(buildApiURL(agent, view, 'cpu/percent'), { signal }),
      fetch(buildApiURL(agent, view, 'memory/virtual'), { signal }),
      fetch(buildApiURL(agent, view, 'memory/swap'), { signal })
    ]);
    var data = {};
    data.cpu = await resp.json();
    data.mem = await respMem.json();
    data.swap = await respSwap.json();
    return data;
  } catch (error) {
    console.log("Error: ", error);
  }

  return {};
};

const fetchDisk = async (agent, units, signal, view) => {
  if (isEmpty(agent)) {
    return {};
  }

  // Connect and get data
  try {
    const [resp] = await Promise.all([
      fetch(buildApiURL(agent, view, 'disk', {
        units: units
      }), { signal })
    ]);
    const data = await resp.json();
    return data;
  } catch (error) {
    console.log("Error: ", error);
  }

  return {};
};

const fetchNetwork = async (agent, signal, view) => {
  if (isEmpty(agent)) {
    return {};
  }

  // Connect and get data
  try {
    const [resp] = await Promise.all([
      fetch(buildApiURL(agent, view, 'network', {
        delta: 1,
        units: 'B'
      }), { signal })
    ]);
    const data = await resp.json();
    return data;
  } catch (error) {
    console.log("Error: ", error);
  }

  return {};
};

const fetchProcesses = async (agent, signal, view) => {
  if (isEmpty(agent)) {
    return {};
  }

  // Connect and get data
  try {
    const [resp] = await Promise.all([
      fetch(buildApiURL(agent, view, 'processes'), { signal })
    ]);
    const data = await resp.json();
    return data;
  } catch (error) {
    console.log("Error: ", error);
  }

  return {};
};

const fetchServices = async (agent, signal, view) => {
  if (isEmpty(agent)) {
    return {};
  }

  // Connect and get data
  try {
    const [resp] = await Promise.all([
      fetch(buildApiURL(agent, view, 'services'), { signal })
    ]);
    const data = await resp.json();
    return data;
  } catch (error) {
    console.log("Error: ", error);
  }

  return {};
};

const fetchUsers = async (agent, signal, view) => {
  if (isEmpty(agent)) {
    return {};
  }

  // Connect and get data
  try {
    const [resp] = await Promise.all([
      fetch(buildApiURL(agent, view, 'system/users'), { signal })
    ]);
    const data = await resp.json();
    return data;
  } catch (error) {
    console.log("Error: ", error);
  }

  return {};
};

const fetchPlugins = async (agent, signal, view) => {
  if (isEmpty(agent)) {
    return {};
  }

  // Connect and get data
  try {
    const [resp] = await Promise.all([
      fetch(buildApiURL(agent, view, 'plugins'), { signal })
    ]);
    const data = await resp.json();
    return data;
  } catch (error) {
    console.log("Error: ", error);
  }

  return {};
};

export function useAgent(agent, view) {
  return useQuery({
    queryKey: [agent, view],
    queryFn: ({ signal }) => fetchSystem(agent, signal, view),
    enabled: agent !== undefined
  });
}

export function useAgentOverview(agent, view) {
  return useQuery({
    queryKey: [agent, view, 'overview'],
    queryFn: ({ signal }) => fetchOverview(agent, signal, view),
    refetchInterval: refreshRates.OVERVIEW,
    enabled: agent !== undefined
  });
}

export function useDisk(agent, view, units = '') {
  return useQuery({
    queryKey: [agent, view, 'disk', units],
    queryFn: ({ signal }) => fetchDisk(agent, units, signal, view),
    refetchInterval: refreshRates.DISK
  });
}

export function useNetwork(agent, view) {
  return useQuery({
    queryKey: [agent, view, 'network'],
    queryFn: ({ signal }) => fetchNetwork(agent, signal, view),
    refetchInterval: refreshRates.NETWORK
  });
}

export function useProcesses(agent, view) {
  return useQuery({
    queryKey: [agent, view, 'processes'],
    queryFn: ({ signal }) => fetchProcesses(agent, signal, view),
    refetchInterval: refreshRates.PROCESSES
  });
}

export function useServices(agent, view) {
  return useQuery({
    queryKey: [agent, view, 'services'],
    queryFn: ({ signal }) => fetchServices(agent, signal, view),
    refetchInterval: refreshRates.SERVICES
  });
}

export function useUsers(agent, view) {
  return useQuery({
    queryKey: [agent, view, 'users'],
    queryFn: ({ signal }) => fetchUsers(agent, signal, view),
    refetchInterval: refreshRates.USERS
  });
}

export function usePlugins(agent, view) {
  return useQuery({
    queryKey: [agent, view, 'plugins'],
    queryFn: ({ signal }) => fetchPlugins(agent, signal, view),
    refetchInterval: refreshRates.PLUGINS
  });
}
